import React from 'react';
import { heroContent } from '../data/mock';

const Hero = () => {
  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Elegant purple gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-lavender-100 via-lavender-50 to-white" />
      
      {/* 3D Floral decorations - Background layer */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        {/* Top left floral cluster */}
        <div className="absolute -top-10 -left-10 w-80 h-80">
          <div className="absolute top-10 left-10 w-40 h-40 bg-lavender-200/40 rounded-full blur-3xl" />
          <div className="absolute top-20 left-20 w-32 h-32 bg-lilac-200/30 rounded-full blur-2xl" />
          {/* Petal shapes */}
          <div className="absolute top-16 left-16 w-8 h-16 bg-lavender-300/40 rounded-full blur-[2px] rotate-45" />
          <div className="absolute top-20 left-8 w-6 h-12 bg-violet-200/30 rounded-full blur-[2px] -rotate-12" />
          <div className="absolute top-8 left-24 w-6 h-12 bg-lilac-300/35 rounded-full blur-[2px] rotate-[70deg]" />
        </div>
        
        {/* Top right floral cluster */}
        <div className="absolute -top-10 -right-10 w-96 h-96">
          <div className="absolute top-10 right-10 w-48 h-48 bg-lilac-200/35 rounded-full blur-3xl" />
          <div className="absolute top-24 right-24 w-36 h-36 bg-lavender-100/40 rounded-full blur-2xl" />
          {/* Petal shapes */}
          <div className="absolute top-20 right-16 w-10 h-20 bg-lavender-300/35 rounded-full blur-[2px] -rotate-45" />
          <div className="absolute top-12 right-28 w-8 h-14 bg-violet-200/30 rounded-full blur-[2px] rotate-12" />
          <div className="absolute top-28 right-8 w-6 h-12 bg-lilac-300/30 rounded-full blur-[2px] -rotate-[70deg]" />
        </div>
        
        {/* Bottom floral accents */}
        <div className="absolute bottom-20 left-[10%] w-24 h-24 bg-lavender-200/30 rounded-full blur-2xl" />
        <div className="absolute bottom-32 right-[15%] w-32 h-32 bg-lilac-200/25 rounded-full blur-2xl" />
        <div className="absolute bottom-10 left-[30%] w-20 h-20 bg-violet-100/30 rounded-full blur-xl" />
        
        {/* Decorative floating petals */}
        <div className="absolute top-[30%] right-[8%] w-6 h-12 bg-lavender-300/25 rounded-full blur-[1px] rotate-[30deg] floral-accent" />
        <div className="absolute top-[40%] left-[5%] w-5 h-10 bg-lilac-300/25 rounded-full blur-[1px] -rotate-[20deg] floral-accent" />
        <div className="absolute bottom-[35%] right-[12%] w-4 h-8 bg-violet-200/30 rounded-full blur-[1px] rotate-[60deg] floral-accent" />
      </div>
      
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        {/* Logo with 3D effect */}
        <div className="mb-10 animate-fade-in">
          <div className="relative inline-block">
            {/* Floral glow behind logo */}
            <div className="absolute -inset-8 bg-gradient-radial from-lavender-300/40 via-lilac-200/30 to-transparent rounded-full blur-2xl animate-pulse-glow" />
            <div className="absolute -inset-4 bg-lavender-50/60 rounded-3xl blur-xl" />
            <img
              src="https://customer-assets.emergentagent.com/job_54544642-256c-49a8-b79e-1e945d33b744/artifacts/vlf8i0qw_0.jpg"
              alt="Hem of Grace Events - Florida Luxury Wedding and Event Planner"
              className="relative mx-auto w-64 sm:w-80 h-auto rounded-2xl shadow-2xl shadow-lavender-900/20 transform hover:scale-[1.02] hover:-translate-y-1 transition-all duration-500"
            />
          </div>
        </div>
        
        {/* Main Heading - SEO H1 */}
        <h1
          className="text-4xl sm:text-5xl md:text-6xl text-grape-800 mb-4 animate-fade-in-delay leading-tight"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
        >
          {heroContent.title}
        </h1>
        
        {/* Tagline */}
        <p
          className="text-xl sm:text-2xl text-lavender-700 mb-6 animate-fade-in-delay"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
        >
          {heroContent.subtitle}
        </p>
        
        {/* Description */}
        <p
          className="text-lg text-grape-600/80 max-w-2xl mx-auto mb-10 animate-fade-in-delay-2"
          style={{ fontFamily: "'Lato', sans-serif" }}
        >
          {heroContent.description}
        </p>
        
        {/* CTA Button with 3D effect - Purple */}
        <div className="relative inline-block animate-fade-in-delay-3">
          {/* Button glow */}
          <div className="absolute -inset-1 bg-gradient-to-r from-lavender-400/60 via-violet-400/50 to-lavender-400/60 rounded-full blur-lg opacity-70" />
          <button
            onClick={scrollToContact}
            className="relative btn-shine inline-flex items-center px-10 py-4 bg-gradient-to-r from-lavender-500 to-violet-600 text-white rounded-full font-medium text-lg shadow-xl shadow-lavender-600/30 hover:from-lavender-600 hover:to-violet-700 transform hover:-translate-y-1 hover:shadow-2xl transition-all duration-300"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.15rem' }}
          >
            {heroContent.ctaText}
          </button>
        </div>
        
        {/* Location + Bilingual Badge */}
        <div className="mt-10 animate-fade-in-delay-3">
          <span className="inline-flex items-center px-5 py-2.5 bg-white/70 backdrop-blur-sm rounded-full text-grape-600 text-sm shadow-lg shadow-lavender-200/50 border border-lavender-200/50">
            <svg className="w-4 h-4 mr-2 text-lavender-500" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="mr-1">Based in Florida •</span>
            <span className="italic">Se habla español</span>
            <span className="hidden sm:inline">&nbsp;(She speaks Spanish)</span>
          </span>
        </div>
      </div>
    </section>
  );
};

export default Hero;
